import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchFoodNormalizationReviewQueue,
  fetchFoodProducts,
  runFoodNormalization,
  updateFoodNormalizationObservation,
} from "../../../../api/foodApi";
import { FOOD_SEEDED_SOURCE_KEYS } from "../../../../constants/foodSourceConstants";
import AdminTableContainer from "../layout/AdminTableContainer";

function confidenceBadge(confidence) {
  if (confidence == null) return "—";
  const pct = Math.round(confidence * 100);
  let className = "food-norm-confidence food-norm-confidence--low";
  if (pct >= 85) className = "food-norm-confidence food-norm-confidence--high";
  else if (pct >= 55) className = "food-norm-confidence food-norm-confidence--medium";
  return <span className={className}>{pct}%</span>;
}

function formatCop(value) {
  if (value == null) return "—";
  return `COP ${Math.round(Number(value)).toLocaleString("es-CO")}`;
}

const FoodNormalizationPage = ({ onClose }) => {
  const [sourceKey, setSourceKey] = useState("EXITO_SUPERMARKET");
  const [statusFilter, setStatusFilter] = useState("NEEDS_REVIEW");
  const [dryRun, setDryRun] = useState(true);
  const [limit, setLimit] = useState(100);
  const [queue, setQueue] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [runSummary, setRunSummary] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const loadQueue = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [queueData, productData] = await Promise.all([
        fetchFoodNormalizationReviewQueue({
          sourceKey: sourceKey || undefined,
          status: statusFilter,
          limit: 50,
        }),
        fetchFoodProducts(),
      ]);
      setQueue(queueData.items || []);
      setProducts(productData.products || []);
    } catch (error) {
      setQueue([]);
      setErrorMessage(error.message || "Unable to load normalization review queue.");
    } finally {
      setLoading(false);
    }
  }, [sourceKey, statusFilter]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const selectedItem = useMemo(
    () => queue.find((item) => item.id === selectedId) || null,
    [queue, selectedId]
  );

  const handleRun = async () => {
    try {
      setRunning(true);
      setErrorMessage("");
      const summary = await runFoodNormalization({
        sourceKey: sourceKey || undefined,
        limit: Number(limit) || 100,
        dryRun,
        forceReprocess: false,
      });
      setRunSummary(summary);
      if (!dryRun) {
        await loadQueue();
      }
    } catch (error) {
      setErrorMessage(error.message || "Normalization run failed.");
    } finally {
      setRunning(false);
    }
  };

  const handleManualMatch = async (observationId, productId, productName) => {
    try {
      await updateFoodNormalizationObservation(observationId, {
        normalizedProductId: productId,
        normalizedProductName: productName,
        normalizationStatus: "MANUALLY_MATCHED",
      });
      await loadQueue();
      setSelectedId(null);
    } catch (error) {
      setErrorMessage(error.message || "Unable to save manual match.");
    }
  };

  const handleReject = async (observationId, nextStatus) => {
    try {
      await updateFoodNormalizationObservation(observationId, {
        normalizationStatus: nextStatus,
      });
      await loadQueue();
      setSelectedId(null);
    } catch (error) {
      setErrorMessage(error.message || "Unable to update observation.");
    }
  };

  return (
    <div className="admin-page food-normalization-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Food Data</p>
          <h1>Normalization Review</h1>
          <p className="muted">
            Compare raw supermarket rows with canonical products and unit prices.
          </p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      <section className="panel-card food-normalization-page__controls">
        <h2>Run normalization</h2>
        <div className="food-normalization-page__control-row">
          <label>
            Source
            <select value={sourceKey} onChange={(e) => setSourceKey(e.target.value)}>
              <option value="">All sources</option>
              {FOOD_SEEDED_SOURCE_KEYS.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
          </label>
          <label>
            Limit
            <input
              type="number"
              min={1}
              max={1000}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
            />
          </label>
          <label className="food-normalization-page__checkbox">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Dry run (no database writes)
          </label>
          <button
            type="button"
            className="btn btn--primary"
            disabled={running}
            onClick={handleRun}
          >
            {running ? "Running…" : dryRun ? "Dry run normalization" : "Run normalization"}
          </button>
        </div>
        {runSummary ? (
          <pre className="food-normalization-page__summary">
            {JSON.stringify(runSummary, null, 2)}
          </pre>
        ) : null}
      </section>

      <section className="panel-card">
        <div className="food-normalization-page__control-row">
          <label>
            Queue status
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="NEEDS_REVIEW">NEEDS_REVIEW</option>
              <option value="PENDING">PENDING</option>
              <option value="AUTO_MATCHED">AUTO_MATCHED</option>
              <option value="MANUALLY_MATCHED">MANUALLY_MATCHED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="UNSUPPORTED">UNSUPPORTED</option>
            </select>
          </label>
          <button type="button" className="btn btn--ghost" onClick={loadQueue}>
            Refresh queue
          </button>
        </div>

        {errorMessage ? <p className="food-dashboard__error">{errorMessage}</p> : null}
        {loading ? <p className="muted">Loading review queue…</p> : null}

        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Raw product</th>
                <th>Brand</th>
                <th>Price</th>
                <th>Proposed match</th>
                <th>Package</th>
                <th>Unit price</th>
                <th>Confidence</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {queue.map((item) => (
                <tr key={item.id}>
                  <td>{item.rawProductName}</td>
                  <td>{item.brand || "—"}</td>
                  <td>{formatCop(item.priceCop)}</td>
                  <td>
                    {item.proposedCanonicalProduct?.displayName ||
                      item.normalizedProductName ||
                      "—"}
                  </td>
                  <td>
                    {item.normalizedPackageQuantity != null
                      ? `${item.normalizedPackageQuantity} ${item.normalizedPackageUnit || ""}`
                      : "—"}
                  </td>
                  <td>{formatCop(item.normalizedUnitPriceCop)}</td>
                  <td>{confidenceBadge(item.normalizationConfidence)}</td>
                  <td>{item.normalizationStatus}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setSelectedId(item.id)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      </section>

      {selectedItem ? (
        <section className="panel-card food-normalization-page__review">
          <h2>Manual review</h2>
          <p>
            <strong>Raw:</strong> {selectedItem.rawProductName}
          </p>
          <p>
            <strong>Notes:</strong> {selectedItem.normalizationNotes || "—"}
          </p>
          <div className="food-normalization-page__manual-actions">
            {products
              .filter((p) => p.isStaple)
              .map((product) => (
                <button
                  key={product.id}
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() =>
                    handleManualMatch(
                      selectedItem.id,
                      product.id,
                      product.normalizedName
                    )
                  }
                >
                  Match: {product.normalizedName}
                </button>
              ))}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => handleReject(selectedItem.id, "REJECTED")}
            >
              Reject
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => handleReject(selectedItem.id, "UNSUPPORTED")}
            >
              Unsupported
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default FoodNormalizationPage;
